import { useEffect, useMemo, useRef } from "react"

import DOMPurify from "dompurify"
import parse from "html-react-parser"

import { useFileContentAsText } from "@/hooks/use-file-content"
import {
  clearHighlights,
  highlightTextInContainer,
  scrollCitationHighlightIntoView,
} from "@/utils/dom-highlight"

import { FallbackViewer } from "./fallback-viewer"
import styles from "../styles/file-viewer.module.css"

type HtmlViewerProps = {
  fileUrl: string
  highlightQuote?: string | null
  highlightChunk?: string | null
  highlightSnippet?: string | null
  highlightScrollContainer?: HTMLElement | null
}

export function HtmlViewer({
  fileUrl,
  highlightQuote = null,
  highlightChunk = null,
  highlightSnippet = null,
  highlightScrollContainer = null,
}: HtmlViewerProps) {
  const { data: html, isError, isLoading } = useFileContentAsText(fileUrl)
  const contentRef = useRef<HTMLDivElement>(null)
  const highlightSnippets = useMemo(
    () =>
      [highlightQuote, highlightChunk, highlightSnippet].filter(
        (snippet): snippet is string => typeof snippet === "string" && snippet.trim().length > 0
      ),
    [highlightQuote, highlightChunk, highlightSnippet]
  )

  useEffect(() => {
    const container = contentRef.current
    if (!container) return
    let cancelScroll = () => {}
    clearHighlights(container)

    if (highlightSnippets.length === 0) return

    let highlighted = false
    for (const snippet of highlightSnippets) {
      const result = highlightTextInContainer(container, snippet)
      if (result.found) {
        highlighted = true
        break
      }
      clearHighlights(container)
    }

    if (highlighted) {
      const mark = container.querySelector("mark.citation-highlight")
      if (mark instanceof HTMLElement) {
        cancelScroll = scrollCitationHighlightIntoView(mark, {
          preferredContainer: highlightScrollContainer ?? container,
          maxPasses: 2,
        })
      }
    }

    return () => {
      cancelScroll()
      if (contentRef.current) clearHighlights(contentRef.current)
    }
  }, [highlightScrollContainer, highlightSnippets, html])

  if (isError) {
    return <FallbackViewer fileUrl={fileUrl} />
  }

  if (isLoading) {
    return (
      <div className={styles.infoContainer}>
        <div className={styles.infoInnerContainer}>
          <h2 style={{ margin: 0, padding: "0 0.5rem" }}>Loading...</h2>
        </div>
      </div>
    )
  }

  const cleanHtml = html ? DOMPurify.sanitize(html) : ""
  return (
    <div className={styles.infoContainer}>
      <div ref={contentRef} className={styles.documentContent}>
        {parse(cleanHtml)}
      </div>
    </div>
  )
}
